import { ErrorAlert } from "@/components/error";
import { AnalyticsEvents, track } from "@/lib/analytics";
import { PendingBillingComponent } from "@/components/pending";
import { authClient } from "@/lib/auth-client";
import { orpc } from "@/lib/orpc";
import { AuthUIContext } from "@papier/auth-ui";
import { Button } from "@papier/ui/components/ui/button";
import { Alert, AlertDescription } from "@papier/ui/components/ui/alert";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { formatDate } from "date-fns";
import { Check, Card as CreditCard } from "@papier/ui/components/icons";
import { OrganizationPageHeader } from "@/components/orgs/organization-page";
import { use, useEffect, useRef } from "react";
import { toast } from "sonner";
import { z } from "zod";

export const Route = createFileRoute("/_app/organization/billing")({
  component: RouteComponent,
  validateSearch: z.object({
    plan: z.literal("pro").optional(),
  }),
  head: () => ({ meta: [{ title: "Papier - Billing & usage" }] }),
});

function RouteComponent() {
  const { useNavigate, useSearch } = Route;
  const navigate = useNavigate();
  const { plan } = useSearch();
  const {
    hooks: { useHasPermission },
  } = use(AuthUIContext);

  const { data: activeOrganization } = authClient.useActiveOrganization();
  const { data: hasPermission, isPending: isPermissionPending } = useHasPermission({
    organizationId: activeOrganization?.id,
    permissions: {
      organization: ["update"],
    },
  });

  const {
    data: billing,
    isPending,
    error,
    refetch,
  } = useQuery({
    ...orpc.subscriptions.getBillingState.queryOptions(),
    queryKey: [...orpc.subscriptions.getBillingState.key(), activeOrganization?.id],
    enabled: !!activeOrganization?.id,
  });
  const subscription = billing?.subscription;
  const isProPlan = billing?.entitlements.plan === "pro";
  const hasOpenSubscription =
    !!subscription &&
    subscription.status !== "canceled" &&
    subscription.status !== "incomplete_expired";
  const autoCheckoutStarted = useRef(false);

  const {
    mutate: createBilling,
    isPending: isBillingPending,
    isSuccess: isBillingSuccess,
  } = useMutation(
    orpc.subscriptions.createBillingPortalSession.mutationOptions({
      onSuccess: (url) => {
        track(AnalyticsEvents.BillingPortalOpened);
        return navigate({ href: url });
      },
      onError: (error) => {
        toast.error("Failed to create billing session", {
          description: error.message,
        });
      },
    }),
  );

  const {
    mutate: createCheckout,
    isPending: isCheckoutPending,
    isSuccess: isCheckoutSuccess,
  } = useMutation(
    orpc.subscriptions.createCheckoutSession.mutationOptions({
      onSuccess: (url) => {
        track(AnalyticsEvents.CheckoutStarted);
        return navigate({ href: url || undefined });
      },
      onError: (error) => {
        toast.error("Failed to create checkout session", {
          description: error.message,
        });
      },
    }),
  );

  useEffect(() => {
    if (
      !plan ||
      isPending ||
      isPermissionPending ||
      error ||
      !billing ||
      !hasPermission?.success ||
      isProPlan ||
      hasOpenSubscription ||
      billing.needsCustomerMigration ||
      autoCheckoutStarted.current
    )
      return;
    autoCheckoutStarted.current = true;
    // Do not retain the auto-upgrade trigger in Checkout's cancel/return URL.
    void navigate({ search: {}, replace: true }).then(() => createCheckout(undefined));
  }, [
    billing,
    createCheckout,
    error,
    hasOpenSubscription,
    hasPermission?.success,
    isPending,
    isPermissionPending,
    isProPlan,
    navigate,
    plan,
  ]);

  if (isPending || isPermissionPending) {
    return <PendingBillingComponent />;
  }
  if (error) {
    return (
      <div className="space-y-3">
        <ErrorAlert error={error.message} />
        <Button variant="outline" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const statusDescriptions: Record<string, string> = {
    incomplete: "Checkout is not complete. Resume checkout to activate your subscription.",
    past_due:
      "A payment is overdue. Open billing to update your payment method or review invoices.",
    unpaid: "Your subscription has unpaid invoices. Open billing to review your payments.",
    paused: "Your subscription is paused. Open billing to review its status.",
  };
  return (
    <main className="w-full min-w-0 space-y-7">
      <OrganizationPageHeader
        title="Billing & usage"
        description="Review your organization's plan, monitor sending usage, and manage billing details."
      />
      {billing?.needsCustomerMigration && (
        <Alert variant="warning">
          <AlertDescription>
            Billing needs an organization-specific customer migration. Contact support before
            changing your subscription.
          </AlertDescription>
        </Alert>
      )}
      {subscription && !isProPlan && hasOpenSubscription && (
        <Alert variant="warning">
          <AlertDescription>
            {statusDescriptions[subscription.status ?? ""] ??
              `Subscription status: ${subscription.status ?? "unknown"}. Open billing to review your subscription.`}
          </AlertDescription>
        </Alert>
      )}
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-6">
          <section
            aria-label="Current plan"
            className="overflow-hidden rounded-xl border bg-background"
          >
            <div className="flex flex-wrap items-center justify-between gap-4 border-b p-5 sm:p-6">
              <div className="flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-xl border bg-primary/4 text-primary">
                  <CreditCard className="size-4" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-xs text-muted-foreground">Current plan</p>
                  <h2 className="mt-1 text-xl font-semibold capitalize">
                    {billing?.entitlements.plan ?? "free"} plan
                  </h2>
                </div>
              </div>
              {!isProPlan && !hasOpenSubscription && (
                <div className="text-right">
                  <p className="text-xl font-semibold">$0</p>
                  <p className="mt-1 text-xs text-muted-foreground">Per month</p>
                </div>
              )}
            </div>
            <div className="space-y-5 p-5 sm:p-6">
              <p className="text-sm text-muted-foreground">
                {isProPlan && subscription?.periodEnd ? (
                  subscription.cancelAtPeriodEnd || subscription.cancelAt ? (
                    <>
                      Scheduled for cancellation:{" "}
                      {formatDate(subscription.cancelAt ?? subscription.periodEnd, "MMM d, yyyy")}
                    </>
                  ) : (
                    <>
                      {subscription.status === "trialing"
                        ? "Trial period ends"
                        : "Current period ends"}
                      : {formatDate(subscription.periodEnd, "MMM d, yyyy")}
                    </>
                  )
                ) : hasOpenSubscription ? (
                  "Review your subscription and payment details in billing."
                ) : (
                  "No current subscription"
                )}
              </p>
              <div className="border-t pt-5">
                <h3 className="text-sm font-semibold">Included in your plan</h3>
                <ul className="mt-4 grid gap-3 text-xs sm:grid-cols-2">
                  {Object.entries(billing?.entitlements.features ?? {})
                    .filter(([, enabled]) => enabled)
                    .map(([feature]) => (
                      <li key={feature} className="flex items-center gap-2">
                        <Check className="size-3.5 text-primary" aria-hidden="true" />
                        {featureLabels[feature] ?? feature}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </section>
          <SendingUsage limits={billing?.entitlements.limits} />
        </div>
        <aside className="space-y-4 rounded-xl border bg-background p-5">
          <h2 className="text-sm font-semibold">Manage your subscription</h2>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Payment methods, invoices, and subscription changes are available in the billing portal.
          </p>
          <div className="flex flex-col gap-2">
            {billing?.canManageBilling && (
              <Button
                disabled={
                  !hasPermission?.success ||
                  billing?.needsCustomerMigration ||
                  isBillingPending ||
                  isBillingSuccess
                }
                isLoading={isBillingPending || isBillingSuccess}
                onClick={() => createBilling(undefined)}
              >
                Manage Billing
              </Button>
            )}
            {!isProPlan && (!hasOpenSubscription || subscription?.status === "incomplete") && (
              <Button
                disabled={
                  !hasPermission?.success ||
                  billing?.needsCustomerMigration ||
                  isCheckoutPending ||
                  isCheckoutSuccess
                }
                isLoading={isCheckoutPending || isCheckoutSuccess}
                onClick={() => createCheckout(undefined)}
              >
                {subscription?.status === "incomplete" ? "Resume Checkout" : "Upgrade Plan"}
              </Button>
            )}
            <Button asChild variant="outline">
              <a href="https://usepapier.io/pricing" target="_blank" rel="noopener noreferrer">
                View plans
              </a>
            </Button>
          </div>
          {!hasPermission?.success && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              An organization owner or administrator can manage billing.
            </p>
          )}
          <p className="border-t pt-4 text-xs leading-relaxed text-muted-foreground">
            Pricing and invoices are shown in billing before you confirm any changes.
          </p>
        </aside>
      </div>
    </main>
  );
}

const featureLabels: Record<string, string> = {
  projects: "Projects",
  templates: "Template editor",
  publishing: "Publishing",
  environments: "Environments",
  apiKeys: "API keys",
  cliSync: "CLI sync",
  media: "Media library",
  previews: "Previews",
  papierEmail: "Email delivery through Papier",
  papierPush: "Web Push through Papier",
  providerConnections: "Your own sending providers",
};

function SendingUsage({ limits }: { limits: Record<string, number | null> | undefined }) {
  const usage = useQuery({
    ...orpc.messaging.getUsage.queryOptions(),
    enabled: limits !== undefined,
  });
  const data = usage.data;
  const meters =
    data &&
    typeof data.papierEmail === "number" &&
    typeof data.papierPush === "number" &&
    typeof data.connection === "number"
      ? [
          {
            label: "Papier email",
            used: data.papierEmail,
            limit: limits?.["papierEmailPerMonth"] ?? null,
          },
          {
            label: "Papier push",
            used: data.papierPush,
            limit: limits?.["papierPushPerMonth"] ?? null,
          },
          {
            label: "Provider messages",
            used: data.connection,
            limit: limits?.["connectionMessagesPerMonth"] ?? null,
          },
        ]
      : [];
  return (
    <section
      aria-labelledby="sending-usage-title"
      className="overflow-hidden rounded-xl border bg-background"
    >
      <div className="border-b p-5 sm:px-6">
        <h2 id="sending-usage-title" className="text-sm font-semibold">
          Sending usage this month
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Messages sent across your organization's projects.
        </p>
      </div>
      <div className="p-5 sm:p-6">
        {usage.isPending ? (
          <p role="status" className="text-sm text-muted-foreground">
            Loading sending usage…
          </p>
        ) : usage.error ? (
          <div className="space-y-3">
            <ErrorAlert error={usage.error.message} />
            <Button variant="outline" size="sm" onClick={() => void usage.refetch()}>
              Retry usage
            </Button>
          </div>
        ) : !meters.length ? (
          <p className="text-sm text-muted-foreground">Sending usage is not available yet.</p>
        ) : (
          <div className="grid gap-5 sm:grid-cols-3">
            {meters.map((meter) => (
              <div key={meter.label} className="min-w-0">
                <p className="text-xs text-muted-foreground">{meter.label}</p>
                <p className="mt-2 text-xl font-semibold">{meter.used.toLocaleString()}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {meter.limit === null
                    ? "No monthly limit"
                    : `of ${meter.limit.toLocaleString()} included`}
                </p>
                {meter.limit !== null && meter.limit > 0 && (
                  <div
                    role="progressbar"
                    aria-label={meter.label}
                    aria-valuemin={0}
                    aria-valuemax={meter.limit}
                    aria-valuenow={Math.min(meter.used, meter.limit)}
                    className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted"
                  >
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${Math.min(100, (meter.used / meter.limit) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
